import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { Company, Customer, Invoice, LineItem, Subscription, Payment, Refund, TermsAndConditions, SignedApproval } from '../types';
import { seedData } from '../utils/seedData';
import { generateId, calculateStripeFee, calculateNetAmount } from '../utils/calculations';
import { callBillingAPI } from '../utils/api';
import { isAuthenticated } from '../utils/session';
interface DataContextType {
  // Data
  companies: Company[];
  isLoadingCompanies: boolean;
  customers: Customer[];
  invoices: Invoice[];
  lineItems: LineItem[];
  subscriptions: Subscription[];
  payments: Payment[];
  refunds: Refund[];
  termsAndConditions: TermsAndConditions[];
  signedApprovals: SignedApproval[];
  // Selected company
  selectedCompanyId: string;
  setSelectedCompanyId: (id: string) => void;
  selectedCompany: Company | undefined;
  // Filtered data (by selected company)
  filteredCustomers: Customer[];
  filteredInvoices: Invoice[];
  filteredSubscriptions: Subscription[];
  // Customer CRUD
  createCustomer: (customer: Omit<Customer, 'id'>) => Promise<Customer>;
  updateCustomer: (id: string, customer: Partial<Customer>) => Promise<Customer>;
  deleteCustomer: (id: string) => Promise<void>;
  getCustomerById: (id: string) => Customer | undefined;
  // Invoice CRUD
  createInvoice: (invoice: Omit<Invoice, 'id'>, items: Omit<LineItem, 'id' | 'invoice_id'>[]) => Promise<Invoice>;
  updateInvoice: (id: string, invoice: Partial<Invoice>, items?: Omit<LineItem, 'id' | 'invoice_id'>[]) => Promise<Invoice>;
  deleteInvoice: (id: string) => Promise<void>;
  getInvoiceById: (id: string) => Invoice | undefined;
  getLineItemsByInvoiceId: (invoiceId: string) => LineItem[];
  // Subscription CRUD
  createSubscription: (subscription: Omit<Subscription, 'id'>, items: Omit<LineItem, 'id' | 'invoice_id'>[]) => Promise<Subscription>;
  updateSubscription: (id: string, subscription: Partial<Subscription>) => Promise<Subscription>;
  deleteSubscription: (id: string) => Promise<void>;
  getSubscriptionById: (id: string) => Subscription | undefined;
  // Payment CRUD
  createPayment: (payment: Omit<Payment, 'id'>) => Promise<Payment>;
  getPaymentsByInvoiceId: (invoiceId: string) => Payment[];
  // Refund CRUD
  createRefund: (refund: Omit<Refund, 'id'>) => Promise<Refund>;
  getRefundsByInvoiceId: (invoiceId: string) => Refund[];
  // Terms & Conditions CRUD
  createTerms: (terms: Omit<TermsAndConditions, 'id' | 'created_at' | 'updated_at'>) => Promise<TermsAndConditions>;
  updateTerms: (id: string, terms: Partial<TermsAndConditions>) => Promise<TermsAndConditions>;
  deleteTerms: (id: string) => Promise<void>;
  getTermsByCompanyId: (companyId: string) => TermsAndConditions[];
  getTermsById: (id: string) => TermsAndConditions | undefined;
  // Signed Approval operations
  createSignedApproval: (approval: Omit<SignedApproval, 'id'>) => Promise<SignedApproval>;
  getApprovalByInvoiceId: (invoiceId: string) => SignedApproval | undefined;
  convertQuoteToInvoice: (invoiceId: string) => Promise<Invoice>;
  // Settings - tax rate is now per company
  taxRate: number; // Returns current company's tax rate as decimal (0-1)
  setTaxRate: (rate: number) => void; // Kept for backward compatibility but deprecated
  refreshCompanies: () => Promise<void>; // Refresh companies from API
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const STORAGE_KEY = 'billing-app-data';
interface StoredData {
  customers: Customer[];
  invoices: Invoice[];
  lineItems: LineItem[];
  subscriptions: Subscription[];
  payments: Payment[];
  refunds: Refund[];
  termsAndConditions: TermsAndConditions[];
  signedApprovals: SignedApproval[];
  selectedCompanyId: string;
  taxRate: number;
}
function loadFromStorage(): StoredData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Convert date strings back to Date objects
      data.invoices = data.invoices.map((inv: Invoice) => ({
        ...inv,
        date: new Date(inv.date)
      }));
      data.subscriptions = data.subscriptions.map((sub: Subscription) => ({
        ...sub,
        start_date: new Date(sub.start_date),
        end_date: sub.end_date ? new Date(sub.end_date) : undefined,
        next_billing_date: new Date(sub.next_billing_date),
        last_billed_date: sub.last_billed_date ? new Date(sub.last_billed_date) : undefined
      }));
      data.payments = data.payments.map((pay: Payment) => ({
        ...pay,
        date: new Date(pay.date)
      }));
      data.refunds = data.refunds.map((ref: Refund) => ({
        ...ref,
        date: new Date(ref.date)
      }));
      return data;
    }
  } catch (e) {
    console.error('Failed to load from storage:', e);
  }
  return null;
}
function saveToStorage(data: StoredData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save to storage:', e);
  }
}
// Simulate async API delay
const delay = (ms: number = 300) => new Promise(resolve => setTimeout(resolve, ms));
interface ApiCompany {
  id: string;
  name: string;
  email: string;
  alias: string;
  stripe_private_key: string;
  stripe_public_key: string;
  tax_rate?: number; // Integer 0-100
  use_wf_orders?: number; // 1 or 0
  wf_api_info?: {
    login: string;
    key: string;
    ecomModuleId: string;
  } | null;
}
export function DataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [legacyTaxRate, setLegacyTaxRate] = useState<number>(0.06); // Kept for backward compatibility
  const [termsAndConditions, setTermsAndConditions] = useState<TermsAndConditions[]>([]);
  const [signedApprovals, setSignedApprovals] = useState<SignedApproval[]>([]);
  // Function to fetch companies from API
  const fetchCompanies = useCallback(async () => {
    // Only fetch companies if user is authenticated
    if (!isAuthenticated()) {
      setIsLoadingCompanies(false);
      return;
    }
    setIsLoadingCompanies(true);
    try {
      const response = await callBillingAPI('getCompanies', {});
      console.log('Fetching companies from API:', response);
      // Handle different possible response structures
      let apiCompanies: ApiCompany[] = [];
      if (Array.isArray(response)) {
        apiCompanies = response;
      } else if (response.companies && Array.isArray(response.companies)) {
        apiCompanies = response.companies;
      } else if (response.data && Array.isArray(response.data)) {
        apiCompanies = response.data;
      } else {
        console.error('Unexpected API response structure:', response);
        throw new Error('Invalid API response format');
      }
      // Transform API companies to match Company type
      const transformedCompanies: Company[] = apiCompanies.map(apiCompany => ({
        id: apiCompany.id,
        name: apiCompany.name,
        stripe_key: apiCompany.stripe_public_key || '',
        tax_rate: apiCompany.tax_rate,
        use_wf_orders: parseInt(String(apiCompany.use_wf_orders)) || 0,
        wf_api_info: apiCompany.wf_api_info
      }));
      console.log('Companies loaded from API:', transformedCompanies);
      setCompanies(transformedCompanies);
      // Set default selected company if not already set
      const stored = loadFromStorage();
      if (stored && stored.selectedCompanyId) {
        // Check if stored company still exists
        const companyExists = transformedCompanies.some(c => c.id === stored.selectedCompanyId);
        if (companyExists) {
          setSelectedCompanyId(stored.selectedCompanyId);
        } else if (transformedCompanies.length > 0) {
          setSelectedCompanyId(transformedCompanies[0].id);
        }
      } else if (transformedCompanies.length > 0) {
        setSelectedCompanyId(transformedCompanies[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch companies from API:', error);
      // Fallback to seed data if API fails
      console.log('Falling back to seed data companies');
      setCompanies(seedData.companies);
      if (seedData.companies.length > 0) {
        setSelectedCompanyId(seedData.companies[0].id);
      }
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);
  // Fetch companies from API
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);
  // Initialize data from storage or seed
  useEffect(() => {
    const stored = loadFromStorage();
    if (stored) {
      setCustomers(stored.customers);
      setInvoices(stored.invoices);
      setLineItems(stored.lineItems);
      setSubscriptions(stored.subscriptions);
      setPayments(stored.payments);
      setRefunds(stored.refunds);
      setTermsAndConditions(stored.termsAndConditions || seedData.termsAndConditions);
      setSignedApprovals(stored.signedApprovals || []);
      setLegacyTaxRate(stored.taxRate);
    } else {
      setCustomers(seedData.customers);
      setInvoices(seedData.invoices);
      setLineItems(seedData.lineItems);
      setSubscriptions(seedData.subscriptions);
      setPayments(seedData.payments);
      setRefunds(seedData.refunds);
      setTermsAndConditions(seedData.termsAndConditions);
      setSignedApprovals([]);
    }
    setIsInitialized(true);
  }, []);
  // Save to storage on changes
  useEffect(() => {
    if (isInitialized && selectedCompanyId) {
      saveToStorage({
        customers,
        invoices,
        lineItems,
        subscriptions,
        payments,
        refunds,
        termsAndConditions,
        signedApprovals,
        selectedCompanyId,
        taxRate: legacyTaxRate
      });
    }
  }, [isInitialized, customers, invoices, lineItems, subscriptions, payments, refunds, termsAndConditions, signedApprovals, selectedCompanyId, legacyTaxRate]);
  const selectedCompany = companies.find(c => c.id === selectedCompanyId);
  // Get tax rate for current company (convert from integer 0-100 to decimal 0-1)
  // Falls back to legacy tax rate if company doesn't have one set
  const taxRate = selectedCompany?.tax_rate !== undefined ? selectedCompany.tax_rate / 100 : legacyTaxRate;
  // Filtered data
  const filteredCustomers = customers.filter(c => c.company_id === selectedCompanyId);
  const filteredInvoices = invoices.filter(i => i.company_id === selectedCompanyId);
  const filteredSubscriptions = subscriptions.filter(s => s.company_id === selectedCompanyId);
  // Customer operations
  const createCustomer = useCallback(async (customer: Omit<Customer, 'id'>): Promise<Customer> => {
    await delay();
    const newCustomer: Customer = {
      ...customer,
      id: generateId('cust-')
    };
    setCustomers(prev => [...prev, newCustomer]);
    return newCustomer;
  }, []);
  const updateCustomer = useCallback(async (id: string, updates: Partial<Customer>): Promise<Customer> => {
    await delay();
    let updated: Customer | undefined;
    setCustomers(prev => prev.map(c => {
      if (c.id === id) {
        updated = {
          ...c,
          ...updates
        };
        return updated;
      }
      return c;
    }));
    if (!updated) throw new Error('Customer not found');
    return updated;
  }, []);
  const deleteCustomer = useCallback(async (id: string): Promise<void> => {
    await delay();
    setCustomers(prev => prev.filter(c => c.id !== id));
  }, []);
  const getCustomerById = useCallback((id: string) => customers.find(c => c.id === id), [customers]);
  // Invoice operations
  const createInvoice = useCallback(async (invoice: Omit<Invoice, 'id'>, items: Omit<LineItem, 'id' | 'invoice_id'>[]): Promise<Invoice> => {
    await delay();
    const invoiceId = generateId('inv-');
    const newInvoice: Invoice = {
      ...invoice,
      id: invoiceId
    };
    const newLineItems: LineItem[] = items.map(item => ({
      ...item,
      id: generateId('li-'),
      invoice_id: invoiceId
    }));
    setInvoices(prev => [...prev, newInvoice]);
    setLineItems(prev => [...prev, ...newLineItems]);
    return newInvoice;
  }, []);
  const updateInvoice = useCallback(async (id: string, updates: Partial<Invoice>, items?: Omit<LineItem, 'id' | 'invoice_id'>[]): Promise<Invoice> => {
    await delay();
    let updated: Invoice | undefined;
    setInvoices(prev => prev.map(i => {
      if (i.id === id) {
        updated = {
          ...i,
          ...updates
        };
        return updated;
      }
      return i;
    }));
    if (!updated) throw new Error('Invoice not found');
    if (items) {
      // Remove old line items and add new ones
      setLineItems(prev => {
        const filtered = prev.filter(li => li.invoice_id !== id);
        const newItems: LineItem[] = items.map(item => ({
          ...item,
          id: generateId('li-'),
          invoice_id: id
        }));
        return [...filtered, ...newItems];
      });
    }
    return updated;
  }, []);
  const deleteInvoice = useCallback(async (id: string): Promise<void> => {
    await delay();
    setInvoices(prev => prev.filter(i => i.id !== id));
    setLineItems(prev => prev.filter(li => li.invoice_id !== id));
  }, []);
  const getInvoiceById = useCallback((id: string) => invoices.find(i => i.id === id), [invoices]);
  const getLineItemsByInvoiceId = useCallback((invoiceId: string) => lineItems.filter(li => li.invoice_id === invoiceId), [lineItems]);
  // Subscription operations
  const createSubscription = useCallback(async (subscription: Omit<Subscription, 'id'>, items: Omit<LineItem, 'id' | 'invoice_id'>[]): Promise<Subscription> => {
    await delay();
    const subscriptionId = generateId('sub-');
    const newSubscription: Subscription = {
      ...subscription,
      id: subscriptionId
    };
    setSubscriptions(prev => [...prev, newSubscription]);
    // Auto-generate first invoice
    const subtotal = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
    const taxableAmount = items.filter(item => item.is_taxable).reduce((sum, item) => sum + item.quantity * item.price, 0);
    const taxAmount = Math.round(taxableAmount * taxRate * 100) / 100;
    const totalAmount = Math.round((subtotal + taxAmount) * 100) / 100;
    const invoiceId = generateId('inv-');
    const newInvoice: Invoice = {
      id: invoiceId,
      company_id: subscription.company_id,
      subscription_id: subscriptionId,
      customer_id: subscription.customer_id,
      date: new Date(),
      status: 'sent',
      subtotal,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total_amount: totalAmount,
      created_by: 'admin',
      sent_via_email: true
    };
    const newLineItems: LineItem[] = items.map(item => ({
      ...item,
      id: generateId('li-'),
      invoice_id: invoiceId
    }));
    setInvoices(prev => [...prev, newInvoice]);
    setLineItems(prev => [...prev, ...newLineItems]);
    return newSubscription;
  }, [taxRate]);
  const updateSubscription = useCallback(async (id: string, updates: Partial<Subscription>): Promise<Subscription> => {
    await delay();
    let updated: Subscription | undefined;
    setSubscriptions(prev => prev.map(s => {
      if (s.id === id) {
        updated = {
          ...s,
          ...updates
        };
        return updated;
      }
      return s;
    }));
    if (!updated) throw new Error('Subscription not found');
    return updated;
  }, []);
  const deleteSubscription = useCallback(async (id: string): Promise<void> => {
    await delay();
    setSubscriptions(prev => prev.filter(s => s.id !== id));
  }, []);
  const getSubscriptionById = useCallback((id: string) => subscriptions.find(s => s.id === id), [subscriptions]);
  // Payment operations
  const createPayment = useCallback(async (payment: Omit<Payment, 'id'>): Promise<Payment> => {
    await delay();
    const newPayment: Payment = {
      ...payment,
      id: generateId('pay-'),
      stripe_fee: payment.method === 'stripe' ? calculateStripeFee(payment.amount) : undefined,
      net_amount: payment.method === 'stripe' ? calculateNetAmount(payment.amount, calculateStripeFee(payment.amount)) : payment.amount
    };
    setPayments(prev => [...prev, newPayment]);
    // Update invoice status if fully paid
    const invoice = invoices.find(i => i.id === payment.invoice_id);
    if (invoice) {
      const existingPayments = payments.filter(p => p.invoice_id === payment.invoice_id && p.status === 'succeeded');
      const totalPaid = existingPayments.reduce((sum, p) => sum + p.amount, 0) + payment.amount;
      if (totalPaid >= invoice.total_amount) {
        setInvoices(prev => prev.map(i => i.id === payment.invoice_id ? {
          ...i,
          status: 'paid'
        } : i));
      }
    }
    return newPayment;
  }, [invoices, payments]);
  const getPaymentsByInvoiceId = useCallback((invoiceId: string) => payments.filter(p => p.invoice_id === invoiceId), [payments]);
  // Refund operations
  const createRefund = useCallback(async (refund: Omit<Refund, 'id'>): Promise<Refund> => {
    await delay();
    const newRefund: Refund = {
      ...refund,
      id: generateId('ref-')
    };
    setRefunds(prev => [...prev, newRefund]);
    // Update invoice status
    setInvoices(prev => prev.map(i => i.id === refund.invoice_id ? {
      ...i,
      status: 'refunded'
    } : i));
    return newRefund;
  }, []);
  const getRefundsByInvoiceId = useCallback((invoiceId: string) => refunds.filter(r => r.invoice_id === invoiceId), [refunds]);
  // Terms & Conditions operations
  const createTerms = useCallback(async (terms: Omit<TermsAndConditions, 'id' | 'created_at' | 'updated_at'>): Promise<TermsAndConditions> => {
    await delay();
    const newTerms: TermsAndConditions = {
      ...terms,
      id: generateId('terms-'),
      created_at: new Date(),
      updated_at: new Date()
    };
    setTermsAndConditions(prev => [...prev, newTerms]);
    return newTerms;
  }, []);
  const updateTerms = useCallback(async (id: string, updates: Partial<TermsAndConditions>): Promise<TermsAndConditions> => {
    await delay();
    let updated: TermsAndConditions | undefined;
    setTermsAndConditions(prev => prev.map(t => {
      if (t.id === id) {
        updated = {
          ...t,
          ...updates,
          updated_at: new Date()
        };
        return updated;
      }
      return t;
    }));
    if (!updated) throw new Error('Terms not found');
    return updated;
  }, []);
  const deleteTerms = useCallback(async (id: string): Promise<void> => {
    await delay();
    setTermsAndConditions(prev => prev.filter(t => t.id !== id));
  }, []);
  const getTermsByCompanyId = useCallback((companyId: string) => termsAndConditions.filter(t => t.company_id === companyId), [termsAndConditions]);
  const getTermsById = useCallback((id: string) => termsAndConditions.find(t => t.id === id), [termsAndConditions]);
  // Signed Approval operations
  const createSignedApproval = useCallback(async (approval: Omit<SignedApproval, 'id'>): Promise<SignedApproval> => {
    await delay();
    const newApproval: SignedApproval = {
      ...approval,
      id: generateId('approval-')
    };
    setSignedApprovals(prev => [...prev, newApproval]);
    return newApproval;
  }, []);
  const getApprovalByInvoiceId = useCallback((invoiceId: string) => signedApprovals.find(a => a.invoice_id === invoiceId), [signedApprovals]);
  const convertQuoteToInvoice = useCallback(async (invoiceId: string): Promise<Invoice> => {
    await delay();
    let converted: Invoice | undefined;
    setInvoices(prev => prev.map(inv => {
      if (inv.id === invoiceId && inv.status === 'quote') {
        converted = {
          ...inv,
          status: 'sent' // Convert to unpaid invoice (sent status)
        };
        return converted;
      }
      return inv;
    }));
    if (!converted) throw new Error('Quote not found or already converted');
    return converted;
  }, []);
  const value: DataContextType = {
    companies,
    isLoadingCompanies,
    customers,
    invoices,
    lineItems,
    subscriptions,
    payments,
    refunds,
    termsAndConditions,
    signedApprovals,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCompany,
    filteredCustomers,
    filteredInvoices,
    filteredSubscriptions,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerById,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    getInvoiceById,
    getLineItemsByInvoiceId,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    getSubscriptionById,
    createPayment,
    getPaymentsByInvoiceId,
    createRefund,
    getRefundsByInvoiceId,
    createTerms,
    updateTerms,
    deleteTerms,
    getTermsByCompanyId,
    getTermsById,
    createSignedApproval,
    getApprovalByInvoiceId,
    convertQuoteToInvoice,
    taxRate,
    setTaxRate: setLegacyTaxRate,
    refreshCompanies: fetchCompanies
  };
  return <DataContext.Provider value={value} data-id="element-388">{children}</DataContext.Provider>;
}
export function useData(): DataContextType {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}